import { Button } from '@/components/UI/Button';
import useCurrentLang from '@/hooks/useCurrentLang';
import useLegacyExternalQuota from '@/hooks/useLegacyExternalQuota';
import LegacyExternalQuotaNotice from '@/components/DmpV2/LegacyExternalQuotaNotice';
import { ConfirmModal } from '@/components/ETrust/CreditReport/RegistrationForm/Modal';
import { leiRenewalView, leiRenewalIntentKey, markLeiRenewalPending, markLeiRenewalConfirmed, markLeiRenewalRejected, hasPendingLeiRenewal, leiRenewalIntentMatches } from '@/utils/leiRenewalIntent';
import { newRequestKey } from '@/components/DmpV2/purchaseIntent';
import { isExternalOutcomeUncertain, isLegacyBalanceReconciliation } from '@/utils/legacyExternalQuota';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageContainer from '@/components/Container/PageContainer';
import Introduction from '@/components/ETrust/LEI/Introduction';
import Information from '@/components/ETrust/LEI/Information';
import Preview from '@/components/ETrust/LEI/Preview';
import useUserDetail from '@/hooks/useUserDetail';
import { getLEI, getLEIOptionList, renewLEI } from '@/api/lei.api';
import RegistrationStatus from '@/components/ETrust/LEI/RegistrationStatus';
import Stepline from '@/components/ETrust/Stepline';
import { Box, LoadingPage } from '@/layout';
import { errorHandler } from '@/utils/toastHandler';
import { NeedUpgradePlanModal, NotEnoughQuotaModal } from '@/components/DocLibrary/Modal';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { RawDataToFormData } from '@/components/ETrust/LEI/Preview/convertLEIFormData';
import usePermissionCheck from '@/hooks/usePermissionCheck';

const LEI = () => {
  // @ts-ignore
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState<number>(0);
  const stepline = t('pages.eTrust.LEI.stepline', { returnObjects: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(searchParams.get('step'));
  const [LEIOptions, setLEIOptions] = useState<LEIResponse>({} as LEIResponse);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<number>(-99);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const [showNotEnoughQuota, setShowNotEnoughQuota] = useState<boolean>(false);
  const { isMainAccount } = usePermissionCheck();
  const { userData } = useAppSelector(getAuthSelector);
  const english = useCurrentLang() === 'en';
  const renewalActor = useRef(String(userData.id || '')); renewalActor.current = String(userData.id || '');
  const [nativeRecord, setNativeRecord] = useState<LEIOriginalData | null>(null);
  const [renewDialog, setRenewDialog] = useState(false);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState('');
  const [renewLocalHold, setRenewLocalHold] = useState(false);
  const renewLock = useRef(false);
  const renewalQuota = useLegacyExternalQuota('lei', userData.user_feature?.LEIApplicantTimes, nativeRecord !== null);
  const renewalHeld = renewLocalHold || leiRenewalView(nativeRecord?.legacy_renewal_state) === 'hold';
  // 此頁不會進行使用者資料的更新, 故activeStep填固定值避免更新
  const userDetail = useUserDetail({});

  const [data, setData] = useState<LEIRequestData>({
    basicInformation: null,
    address: null,
    legalBusiness: null,
    directParent: null,
    ultimateParent: null,
  });

  const initData = useCallback(async () => {
    setIsLoading(true);
    if (LEIOptions.EntityLegalCategory) return;
    try {
      const res = (await getLEIOptionList()).data;
      if (res.success) {
        setLEIOptions(res.data);
        checkProgress(res.data);
      }
    } catch (e) {
      setRenewError(english ? 'Unable to load the original application. Check again before continuing.' : '無法讀取原申請，請重新查詢後再繼續。');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkProgress = useCallback(async (options: LEIResponse) => {
    const expectedActor = renewalActor.current;
    setIsLoading(true);
    try {
      const res: { data: LEIOriginalData } = (await getLEI()).data;
      if (expectedActor !== renewalActor.current) return;
      // 若status大於等於0, 則先進行表單資料回填, can_submit
      if (res.data.status !== -99) {
        setNativeRecord(res.data);
        const renewalView = leiRenewalView(res.data.legacy_renewal_state);
        const actor = renewalActor.current;
        const pendingDelivery = hasPendingLeiRenewal(window.sessionStorage, actor);
        const sameIntent = leiRenewalIntentMatches(window.sessionStorage, actor, res.data.legacy_renewal_intent_key_hash);
        if (sameIntent && ['draft','submitted'].includes(res.data.legacy_renewal_state || '')) markLeiRenewalConfirmed(window.sessionStorage, actor);
        if (pendingDelivery && !(sameIntent && ['draft','submitted'].includes(res.data.legacy_renewal_state || ''))) setRenewLocalHold(true);
        setStatus(renewalView === 'preview' ? 0 : res.data.status);
        const storeData = RawDataToFormData(res.data, options);
        setData(storeData);
        if (pendingDelivery && !(sameIntent && ['draft','submitted'].includes(res.data.legacy_renewal_state || ''))) return;
        if (renewalView === 'preview') { navigate('/lei?step=3', { replace: true }); setCurrentStep('3'); setActiveStep(2); return; }
        if (res.data.legacy_renewal_state === 'submitted') { navigate('/lei?step=5', { replace: true }); setCurrentStep('5'); setActiveStep(4); return; }
        if (renewalView === 'hold') { navigate('/lei?step=1', { replace: true }); setCurrentStep('1'); setActiveStep(0); return; }
        if (res.data.status === 0) return;
        if (res.data.status === 2) return;
        if (res.data.status === 7) return;
        navigate('/lei?step=5', { replace: true });
        setCurrentStep('5');
        setActiveStep(4);
        return;
      }
    } catch (error) {
      if (expectedActor !== renewalActor.current) return;
      errorHandler(t('error.server'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!searchParams.get('step')) {
      navigate('/lei?step=1', { replace: true });
      setCurrentStep('1');
      setActiveStep(0);
      return;
    }

    if (!currentStep || parseInt(currentStep) === 0) {
      setCurrentStep('1');
      navigate('/lei?step=1', { replace: true });
      setActiveStep(0);
    } else {
      const paramsStep = parseInt(currentStep);
      setActiveStep(paramsStep - 1);
    }
  }, [searchParams.get('step')]);

  useEffect(() => {
    initData();
  }, [initData]);

  const checkLevel = () => {
    // if (userData.plan.level <= 1) {
    //   setShowUpgradeModal(true);
    //   navigate('/lei?step=1', { replace: true });
    //   setCurrentStep('1');
    //   setActiveStep(0);
    //   return false;
    // }
    if (!isMainAccount) {
      setCurrentStep('1');
      navigate('/lei?step=1', { replace: true });
      setActiveStep(0);
      errorHandler(t('error.permission'));
    }
    return isMainAccount;
  };

  const pendingRenewalFor = (record: LEIOriginalData) => {
    try {
      const actor = String(userData.id); const pending = hasPendingLeiRenewal(window.sessionStorage, actor);
      const matches = leiRenewalIntentMatches(window.sessionStorage, actor, record.legacy_renewal_intent_key_hash);
      if (matches && ['draft','submitted'].includes(record.legacy_renewal_state || '')) { markLeiRenewalConfirmed(window.sessionStorage, actor); return false; }
      return pending;
    } catch { return renewLocalHold; }
  };

  const showCanonicalDraft = (record: LEIOriginalData) => {
    setNativeRecord(record); setData(RawDataToFormData(record, LEIOptions)); setStatus(0);
    setRenewLocalHold(false); setRenewDialog(false); setCurrentStep('3'); setActiveStep(2); navigate('/lei?step=3');
  };
  const readRenewal = async () => {
    const response = (await getLEI()).data as { data: LEIOriginalData };
    if (!renewalQuota.isCurrent()) throw new Error('renewal_actor_changed');
    setNativeRecord(response.data); return response.data;
  };
  const refreshRenewal = async () => {
    setRenewBusy(true); setRenewError('');
    try { const record = await readRenewal(); if (pendingRenewalFor(record)) { setRenewLocalHold(true); return; } const view = leiRenewalView(record.legacy_renewal_state); if (view === 'preview') showCanonicalDraft(record); else if (record.legacy_renewal_state === 'submitted') { setRenewLocalHold(false); await checkProgress(LEIOptions); } }
    catch { setRenewError(english ? 'Unable to confirm renewal status. Check again before continuing.' : '無法確認續期狀態，請重新查詢後再繼續。'); }
    finally { setRenewBusy(false); }
  };
  const openRenewal = async () => {
    if (!isMainAccount || !nativeRecord || renewalHeld || renewLock.current) return;
    renewLock.current = true; setRenewBusy(true); setRenewError('');
    try {
      const access = await renewalQuota.refresh();
      if (access.state === 'exhausted') { navigate('/cart?item=15', { state: { from: '/lei?step=1' } }); return; }
      if (access.state !== 'available') return;
      const record = await readRenewal(); if (pendingRenewalFor(record)) { setRenewLocalHold(true); setRenewDialog(false); return; } const view = leiRenewalView(record.legacy_renewal_state);
      if (view === 'preview') { showCanonicalDraft(record); return; }
      if (view === 'hold') { setRenewLocalHold(true); return; }
      if (access.managed && !Object.prototype.hasOwnProperty.call(record, 'legacy_renewal_state')) { setRenewError(english ? 'Unable to confirm renewal status. Check again before continuing.' : '無法確認續期狀態，請重新查詢後再繼續。'); return; }
      setRenewDialog(true);
    } catch { if (!renewalQuota.isCurrent()) return; setRenewError(english ? 'Unable to confirm renewal status. Check again before continuing.' : '無法確認續期狀態，請重新查詢後再繼續。'); }
    finally { renewLock.current = false; setRenewBusy(false); }
  };
  const confirmRenewal = async () => {
    if (renewLock.current || !isMainAccount || renewalHeld) return;
    renewLock.current = true; setRenewBusy(true); setRenewError(''); let nativeAttempted = false; let managedRequest = false;
    try {
      const access = await renewalQuota.refresh();
      if (access.state === 'exhausted') { setRenewDialog(false); navigate('/cart?item=15', { state: { from: '/lei?step=1' } }); return; }
      if (access.state !== 'available') return;
      const record = await readRenewal(); if (pendingRenewalFor(record)) { setRenewLocalHold(true); setRenewDialog(false); return; } const view = leiRenewalView(record.legacy_renewal_state);
      if (view === 'preview') { showCanonicalDraft(record); return; }
      if (view === 'hold') { setRenewLocalHold(true); setRenewDialog(false); return; }
      if (access.managed && !Object.prototype.hasOwnProperty.call(record, 'legacy_renewal_state')) { setRenewLocalHold(true); setRenewDialog(false); return; }
      managedRequest = access.managed === true;
      const key = access.managed ? leiRenewalIntentKey(window.sessionStorage, String(userData.id), newRequestKey, record.legacy_renewal_state === 'submitted') : undefined;
      if (access.managed) markLeiRenewalPending(window.sessionStorage, String(userData.id));
      nativeAttempted = true; const result = await renewLEI(key);
      if (!renewalQuota.isCurrent()) return;
      if (!result.data.success) throw new Error('renewal_not_confirmed');
      const canonical = await readRenewal();
      if (access.managed && pendingRenewalFor(canonical)) { setRenewLocalHold(true); setRenewDialog(false); return; }
      if (leiRenewalView(canonical.legacy_renewal_state) === 'hold') { setRenewLocalHold(true); setRenewDialog(false); return; }
      if (access.managed && canonical.legacy_renewal_state === 'submitted') { setRenewDialog(false); setRenewLocalHold(false); await checkProgress(LEIOptions); return; }
      showCanonicalDraft(canonical);
    } catch (error) {
      if (!renewalQuota.isCurrent()) return;
      setRenewDialog(false);
      if (isLegacyBalanceReconciliation(error)) renewalQuota.markReconciliation('legacy_balance');
      else if (nativeAttempted && isExternalOutcomeUncertain(error)) setRenewLocalHold(true);
      else { if (nativeAttempted && managedRequest) markLeiRenewalRejected(window.sessionStorage, String(userData.id)); setRenewError(english ? 'Unable to create the renewal draft. Check the original status before trying again.' : '無法建立續期草稿，請先確認原申請狀態後再試。'); }
    } finally { renewLock.current = false; setRenewBusy(false); }
  };

  useEffect(() => {}, [t]);

  return (
    <PageContainer title={t('pages.eTrust.LEI.title')}>
      <LoadingPage isLoading={isLoading || renewBusy} />
      {nativeRecord && <LegacyExternalQuotaNotice decision={renewalQuota.decision} retry={renewalQuota.refresh} />}
      {renewError && <p role="alert">{renewError}</p>}
      {renewalHeld && <p role="alert">{english ? 'The original renewal is awaiting confirmation. Do not create another intent or make another payment.' : '原續期申請仍待確認，請勿建立新意圖、重送或再次付款。'}</p>}
      {(renewError || renewalHeld) && <Button type="button" variant="primary-invert" disabled={renewBusy} onClick={() => void refreshRenewal()}>{english ? 'Check original renewal status' : '重新查詢原續期狀態'}</Button>}
      <ConfirmModal showModal={renewDialog} setShowModal={setRenewDialog} onSubmit={() => void confirmRenewal()} hideAllowance busy={renewBusy} message={(english ? 'Create a renewal application draft? Review the information and complete the existing submission process.' : '確認建立續期申請草稿？請檢視資料並依既有流程完成申請。') + (renewalQuota.decision.managed ? (english ? ' Included allowance is consumed only when the provider accepts the submission.' : ' 內含額度在供應商受理提交後才核銷。') : '')} />
      <NeedUpgradePlanModal showModal={showUpgradeModal} setShowModal={setShowUpgradeModal} />
      <NotEnoughQuotaModal showModal={showNotEnoughQuota} setShowModal={setShowNotEnoughQuota} />
      <div style={{ display: 'flex' }}>
        <Stepline activeStep={activeStep} stepline={stepline} type={'LEI'} />
        {activeStep === 0 && (
          <Introduction
            data={data}
            status={status}
            checkLevel={checkLevel}
            renewLabel={english ? 'Create renewal draft' : '申請續期草稿'}
            onRenew={() => void openRenewal()}
            renewalDisabled={!nativeRecord || renewalHeld || renewBusy}
            applicationHeld={renewalHeld}
            setActiveStep={setActiveStep}
            setCurrentStep={setCurrentStep}
          />
        )}
        {activeStep === 1 && !renewalHeld && (
          <Information
            status={status}
            data={data}
            checkLevel={checkLevel}
            setData={setData}
            userDetail={userDetail}
            setActiveStep={setActiveStep}
            setCurrentStep={setCurrentStep}
            LEIOptions={LEIOptions}
          />
        )}
        {activeStep === 2 && !renewalHeld && (
          <Preview
            status={status}
            data={data}
            checkLevel={checkLevel}
            setActiveStep={setActiveStep}
            setCurrentStep={setCurrentStep}
            LEIOptions={LEIOptions}
          />
        )}
        {activeStep === 4 && (
          <RegistrationStatus status={status} setActiveStep={setActiveStep} setCurrentStep={setCurrentStep} renewLabel={english ? 'Create renewal draft' : '申請續期草稿'} onRenew={() => void openRenewal()} renewalDisabled={!nativeRecord || renewalHeld || renewBusy} />
        )}
      </div>
    </PageContainer>
  );
};

export default LEI;
